import React from "react";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import { titleCase } from "title-case";
import { useTranslation } from "react-i18next";
import { Inline, Stack } from "components/layout/reusableLayouts";
import Summary from "../../common/Summary";
import RenderRelationship from "../../common/contacts/RenderRelationship";
import RelationshipType from "../../../enums/RelationshipType";
import { fullName } from "utils/fullName";

/** The shape `DOES_CONTACT_EXIST` returns, which is all this popover reads. */
export type ExistingContact = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  businessName: string | null;
  parentContactId: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  relationshipType: RelationshipType | null;
  address: { addressName: string | null } | null;
};

interface ExistingContactPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: (
    contact: ExistingContact | null,
    shouldReactivate?: boolean,
  ) => void;
  open: boolean;
  existingContact: ExistingContact;
}

// The DS Popover is trigger-owned (it takes a `trigger` render prop and holds
// its own open state), and this one is opened from error helper text and
// anchored to a field wrapper, so it stays on the themed MUI primitive.
const ExistingContactPopover: React.FC<ExistingContactPopoverProps> = ({
  anchorEl,
  onClose,
  open,
  existingContact,
}) => {
  const { t } = useTranslation();

  const handleClose = () => {
    onClose(null);
  };

  const handleCopy = () => {
    onClose(existingContact);
  };

  const handleReactivate = () => {
    onClose(existingContact, true);
  };

  // Summary drops empty values, which is what the eight `&&` guards here used
  // to do by hand.
  const items = [
    {
      title: t("Name"),
      value: fullName(existingContact) || null,
      fullWidth: true,
    },
    {
      title: t("Business Name"),
      value: existingContact.businessName,
      fullWidth: true,
    },
    { title: t("Website"), value: existingContact.website, fullWidth: true },
    { title: t("Email"), value: existingContact.email, fullWidth: true },
    { title: t("Phone"), value: existingContact.phone, fullWidth: true },
    {
      title: t("Address"),
      value: existingContact.address?.addressName ?? null,
      fullWidth: true,
    },
    {
      title: t("Relationship Type"),
      value: (
        <RenderRelationship relationship={existingContact.relationshipType} />
      ),
      fullWidth: true,
    },
    {
      title: t("Status"),
      value: titleCase(existingContact.status.toLowerCase()),
      fullWidth: true,
    },
  ];

  return (
    <Popover
      id={"existing-contact-popover"}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Stack sx={{ padding: "var(--space-5)", width: 512 }}>
        {existingContact.id && (
          <>
            <Summary items={items} disableContainer />
            <Inline justify="center" gap="var(--space-7)">
              <Button variant="danger-filled" onClick={handleClose}>
                {t("Close")}
              </Button>
              <Button variant="secondary-outlined" onClick={handleCopy}>
                {t("Copy Details")}
              </Button>
              {existingContact.status === "DELETED" && (
                <Button variant="primary-filled" onClick={handleReactivate}>
                  {t("Reactivate")}
                </Button>
              )}
            </Inline>
          </>
        )}
      </Stack>
    </Popover>
  );
};

export default ExistingContactPopover;
